import { useState, useEffect } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import PerformanceComponent from "./PerformanceComponent";
import NavbarProductInfo from "../../components/productsInfosComponents/navbarProductInfo";
import FinanceComponentPage from "./FinanceComponentPage";
import BackupComponent from "./backupComponentPage";
import { useInfrastructureCache } from "../../hooks/useInfrastructureCache";
import TroubleshootPage from "./troubleshootPage";

const ProductDetailPage = () => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const { data: infrastructure, isLoading } = useInfrastructureCache();

  const [activeTab, setActiveTab] = useState("Performance");
  const [vmIds, setVmIds] = useState([]);
  const [vmNames, setVmNames] = useState([]);

  const tabs = ["Performance", "Finance", "Troubleshoot", "Backup"];

  const getMobileTabName = (tab) => {
    switch(tab) {
      case "Performance": return "Perf";
      case "Finance": return "Fin";
      case "Troubleshoot": return "Tsht";
      case "Backup": return "Bkp";
      default: return tab;
    }
  };

  const tabComponents = {
    Performance: <PerformanceComponent vmIds={vmIds} />,
    Finance: <FinanceComponentPage vmIds={vmIds} vmNames={vmNames} />,
    Troubleshoot: <TroubleshootPage vmIds={vmIds} vmNames={vmNames}/>,
    Backup: <BackupComponent vmIds={vmIds} />,
  };

  useEffect(() => {
    if (isLoading || !infrastructure) return;

    const { vms, tags, vmTags } = infrastructure;
    let resolvedVmIds = [];
    let resolvedVmNames = [];

    if (location.state?.vmIds) {
      resolvedVmIds = location.state.vmIds;
      resolvedVmNames = location.state.vmNames || [];
      setVmIds(resolvedVmIds);
      setVmNames(resolvedVmNames);
      return;
    }

    const { vmName, tagName, tab } = params;

    if (tab && tabs.includes(tab)) {
      setActiveTab(tab);
    }

    if (vmName) {
      const decodedVmName = decodeURIComponent(vmName);
      const vm = vms.find((v) => v.name === decodedVmName);
      if (vm) {
        resolvedVmIds = [vm.id];
        resolvedVmNames = [vm.name];
      }
    } else if (tagName) {
      const decodedTagName = decodeURIComponent(tagName);
      const tag = tags.find((t) => t.value === decodedTagName);
      if (tag) {
        const relatedVmIds = vmTags
          .filter((relation) => relation.tagId === tag.id)
          .map((relation) => relation.vmId);
        resolvedVmIds = vms
          .filter((vm) => relatedVmIds.includes(vm.id))
          .map((vm) => vm.id);
        resolvedVmNames = vms
          .filter((vm) => relatedVmIds.includes(vm.id))
          .map((vm) => vm.name);
      }
    }

    if (resolvedVmIds.length > 0) {
      setVmIds(resolvedVmIds);
      setVmNames(resolvedVmNames);

      if (!params.vmName && !params.tagName && resolvedVmIds.length === 1) {
        const currentTabFromUrl = params.tab || activeTab;
        navigate(
          `/productDetail/vm/${encodeURIComponent(resolvedVmNames[0])}/${currentTabFromUrl}`,
          {
            replace: true,
            state: { vmIds: resolvedVmIds, vmNames: resolvedVmNames },
          },
        );
      } else if (
        !params.vmName &&
        !params.tagName &&
        resolvedVmIds.length > 1
      ) {
        const firstTag = vmTags.find((rel) => rel.vmId === resolvedVmIds[0]);
        const tag = tags.find((t) => t.id === firstTag?.tagId);
        const tagValue = tag?.value || "group";
        navigate(
          `/productDetail/tag/${encodeURIComponent(tagValue)}/${activeTab}`,
          {
            replace: true,
            state: { vmIds: resolvedVmIds, vmNames: resolvedVmNames },
          },
        );
      }
    }
  }, [
    location.state,
    params,
    infrastructure,
    isLoading,
    navigate,
    tabs,
    activeTab,
  ]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    if (params.vmName) {
      navigate(`/productDetail/vm/${params.vmName}/${tab}`, {
        state: { vmIds, vmNames },
      });
    } else if (params.tagName) {
      navigate(`/productDetail/tag/${params.tagName}/${tab}`, {
        state: { vmIds, vmNames },
      });
    } else if (vmIds.length === 1 && vmNames[0]) {
      navigate(`/productDetail/vm/${encodeURIComponent(vmNames[0])}/${tab}`, {
        state: { vmIds, vmNames },
      });
    } else if (vmIds.length > 1) {
      const firstTag = infrastructure?.vmTags?.find(
        (rel) => rel.vmId === vmIds[0],
      );
      const tag = infrastructure?.tags?.find((t) => t.id === firstTag?.tagId);
      const tagValue = tag?.value || "group";
      navigate(`/productDetail/tag/${encodeURIComponent(tagValue)}/${tab}`, {
        state: { vmIds, vmNames },
      });
    }
  };

  if (isLoading) {
    return (
      <div>
        <NavbarProductInfo />
        <div className="flex h-96 items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-primary-dark/60 text-lg">
              Loading product details...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <NavbarProductInfo />

      <div
        className="tabs-container sticky z-10 bg-white border-b border-border-color"
        style={{ top: "72px" }}
      >
        <div className="px-3 sm:px-6 pt-3 sm:pt-4">
          <div className="hidden sm:flex justify-between">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`
                  flex-1 p-3 sm:p-4 m-1 sm:m-4 border rounded-lg border-primary-dark text-sm sm:text-base font-bold transition-all duration-200
                  ${
                    activeTab === tab
                      ? "text-white bg-primary-dark"
                      : "text-black hover:text-white hover:bg-dark-bg/80"
                  }
                `}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="flex sm:hidden flex-wrap justify-center gap-2">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`
                  px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200 w-[calc(33.33%-0.5rem)]
                  ${
                    activeTab === tab
                      ? "text-white bg-primary-dark"
                      : "text-black border border-gray-300 hover:text-white hover:bg-dark-bg/80"
                  }
                `}
              >
                {getMobileTabName(tab)}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-8">
        {vmIds.length === 0 ? (
          <div className="text-center py-12 bg-card-bg rounded-xl border border-border-color">
            <p className="text-primary-dark/60">
              No VM selected. Please select a product from the dropdown above.
            </p>
          </div>
        ) : (
          tabComponents[activeTab]
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;